/*
 * @providesModule PushNotificationController
 * @format
 */

import AWSManager from 'AWSManager';
import ClientPropertyConstants from 'ClientPropertyConstants';
import DeepLinkUtils from 'DeepLinkUtils';
import FTUEController from 'FTUEController';
import GlobalEventEmitter from 'GlobalEventEmitter';
import ModalNavigationController from 'ModalNavigationController';
import NavigationManager from 'NavigationManager';
import NotificationNavigationController from 'NotificationNavigationController';
import NotificationToast from 'NotificationToast';
import SKZConstants from 'SKZConstants';
import SKZMatch from 'SKZMatch';
import SKZNotification, {
  ChatToast,
  GeneralToast,
  NotificationIconImages,
  NotificationTypes,
  Toast,
  TrophyToast,
} from 'SKZNotification';
import SKZTrophy from 'SKZTrophy';
import {
  fetchTrophyData,
  getProgressedTrophiesToToast,
  getTrophyDataFromStorage,
} from 'TrophyToastDataController';
import ScreenNames from 'ScreenNames';
import _ from 'lodash';
import React from 'react';
import {AppState, NativeModules, Platform} from 'react-native';

const SkillzModule = NativeModules.SkillzModule;

const ApplicationState = {
  ACTIVE: 'active',
  BACKGROUND: 'background',
  INACTIVE: 'inactive',
};
const DeepLinkTypes = SKZConstants.DeepLinkTypes;

// These are the scene names used when pushing on a screen to the React Navigator
// We want to block showing in app toast notifications when these screens are at the top of the route
// stack in JS so as not to interfere in critical flows
const BlockNotificationScreens = [
  'Versus',
  'Checkout Deposit Screen', // Checkout v2
  'checkout', // Legacy checkout
];

class PushNotificationController {
  currentAppState: string = ApplicationState.ACTIVE;
  tournamentIdsToBeIgnored: Array<string> = [];
  storedTournamentResultNotifications: Array<SKZNotification> = [];
  notifToastsToDisplay: Toast[] = [];
  isDisplayingToast: boolean = false;
  canFireNextToast: boolean = false;
  isMatchInProgress: boolean = false;
  queueResultScreenToast: boolean = false;
  intervalTimer: number = 0;
  durationTimer: number = 0;

  constructor() {
    this.addListeners();
  }

  private addListeners() {
    console.debug('PushNotificationController: adding listeners');
    AppState.addEventListener('change', this.handleAppStateChange);
  }

  private handleAppStateChange = (nextAppState: string) => {
    this.currentAppState = nextAppState;
  };

  didReceiveNotification(notificationData: string): void {
    const notification: SKZNotification =
      SKZNotification.createNotificationFromNative(notificationData);
    console.debug(
      'PushNotificationController: did receive notification data: ' + JSON.stringify(notification)
    );
    SkillzModule.shouldInteractWithInterface(
      (shouldInteract: boolean, isMatchInProgress: boolean) => {
        this.isMatchInProgress = isMatchInProgress;
        if (!shouldInteract || notification.isFromSDKLaunch()) {
          console.debug('PushNotificationController: shouldnt interact with interface');
          this.processRemoteNotification(notification);
        } else {
          console.debug('PushNotificationController: should interact with interface');
          if (notification.isActionEqualToType(NotificationTypes.TOURNAMENT_RESULTS)) {
            const notifTournamentId = notification.getTournamentId();
            const ignoreIndex = _.findIndex(this.tournamentIdsToBeIgnored, notifTournamentId);
            const storedIndex = _.findIndex(
              this.storedTournamentResultNotifications,
              (notif: SKZNotification): boolean => {
                return notif.getTournamentId() == notifTournamentId;
              }
            );

            if (ignoreIndex > -1 || storedIndex > -1) {
              return;
            }

            this.shouldStoreTournamentResult((shouldStore: boolean) => {
              console.debug(
                'PushNotificationController: should store tournament result: ' + shouldStore
              );
              if (shouldStore) {
                this.storedTournamentResultNotifications.push(notification);
              } else {
                this.startProcessNotification(notification, shouldInteract);
              }
            });
          } else {
            this.startProcessNotification(notification, shouldInteract);
          }
        }
      }
    );
  }

  private startProcessNotification(notification: SKZNotification, shouldInteract: boolean) {
    if (this.currentAppState !== ApplicationState.ACTIVE) {
      this.processRemoteNotification(notification);
    } else if (shouldInteract) {
      this.processNotification(notification);
    }
  }

  private shouldStoreTournamentResult(completion: (shouldStore: boolean) => void) {
    NavigationManager.checkViewControllerStack()
      .then((isEmptyStack: boolean) => {
        completion(!isEmptyStack);
      })
      .catch(() => {
        completion(true);
      });
  }

  public flagIgnoreTournamentNotification(match: SKZMatch) {
    if (match && match.isCompleted() && match.id) {
      this.tournamentIdsToBeIgnored.push(match.id);
    }
  }

  public processStoredTournamentResults() {
    if (this.storedTournamentResultNotifications.length > 0) {
      this.shouldStoreTournamentResult((shouldStore: boolean) => {
        if (!shouldStore) {
          const tournamentNotif: SKZNotification | undefined = _.head(
            this.storedTournamentResultNotifications
          );
          this.storedTournamentResultNotifications = _.drop(
            this.storedTournamentResultNotifications
          );

          if (tournamentNotif) {
            const indexOfIgnore = _.findIndex(
              this.tournamentIdsToBeIgnored,
              (tournamentId: string): boolean => {
                return tournamentId == JSON.stringify(tournamentNotif.getTournamentId());
              }
            );

            if (indexOfIgnore > -1) {
              this.processStoredTournamentResults();
            } else {
              this.processNotification(tournamentNotif);
            }
          }
        }
      });
    }
  }

  private shouldShowNotificationToast(): boolean {
    let displayedScreens = 0;

    _.forEach(BlockNotificationScreens, (screen: string) => {
      if (NavigationManager.isSceneCurrentlyPresented(screen)) {
        displayedScreens += 1;
      }
    });

    return displayedScreens == 0;
  }

  private processRemoteNotification(notification: SKZNotification) {
    console.debug('PushNotificationController: process remote notification');
    if (notification.isActionEqualToType(NotificationTypes.TROPHY_EARNED)) {
      this.queueTrophyEarnedModalForTrophyId(notification.getTrophyId() || '');
    }

    this.queueResultScreenToast = this.isMatchInProgress;

    const deepLink: string | undefined = notification.getDeepLink();
    if (deepLink != undefined && deepLink.length > 0) {
      if (!this.isMatchInProgress) {
        DeepLinkUtils.handleDeeplink(deepLink, () => {});
      } else {
        this.processNotification(notification);
      }
    } else {
      this.processNotification(notification);
    }
  }

  private processNotification(notification: SKZNotification) {
    console.debug(
      'PushNotificationController: process notification: ' + JSON.stringify(notification)
    );

    if (this.shouldShowNotificationToast() || this.isMatchInProgress) {
      console.debug('PushNotificationController: should show notification toast');
      if (notification.isActionEqualToType(NotificationTypes.PLAYER_LEVEL)) {
        return;
      } else if (notification.isActionEqualToType(NotificationTypes.IN_APP_TOAST)) {
        this.queueInAppToast(notification);
      } else if (notification.isLeaderboardNotification()) {
        const params: Object = {
          action: DeepLinkTypes.SHOW_LEADERBOARDS,
        };
        this.addNotifToastToQueue(
          notification.getMessageTitle() || '',
          notification.getMessageBody(),
          NotificationIconImages.LEADERBOARD,
          params,
          {}
        );
      } else if (notification.isActionEqualToType(NotificationTypes.TOURNAMENT_RESULTS)) {
        this.queueMatchResultsToast(notification);
      } else if (notification.isActionEqualToType(NotificationTypes.TROPHY_EARNED)) {
        this.addTrophyEarnedNotifToQueue(notification);
      } else if (notification.isActionEqualToType(NotificationTypes.NOTIFY_TURN_ACTION)) {
        this.addNotifToastToQueue(
          notification.getMessageTitle() || '',
          notification.getMessageBody(),
          NotificationIconImages.TURN_BASED,
          notification.getAction(),
          {}
        );
      } else if (notification.isActionEqualToType(NotificationTypes.DISPLAY_LINK)) {
        this.addNotifToastToQueue(
          notification.getMessageTitle() || '',
          notification.getMessageBody(),
          NotificationIconImages.GENERIC,
          notification.getAction(),
          notification
        );
      } else if (notification.isActionEqualToType(NotificationTypes.LOCAL_LIVE_EVENT)) {
        this.addNotifToastToQueue(
          notification.getMessageTitle() || '',
          notification.getMessageBody(),
          NotificationIconImages.LIVE_EVENT,
          notification.getAction(),
          notification
        );
      } else if (notification.isActionEqualToType(NotificationTypes.REMATCH)) {
        this.addNotifToastToQueue(
          notification.getMessageTitle() || '',
          notification.getMessageBody(),
          NotificationIconImages.REMATCH,
          notification.getAction(),
          {}
        );
      } else if (notification.isActionEqualToType(NotificationTypes.CHAT)) {
        this.addChatNotifToastToQueue(notification);
      } else if (this.isADeepLinkToast(notification)) {
        this.addNotifToastToQueue(
          notification.getMessageTitle() || '',
          notification.getMessageBody(),
          NotificationIconImages.GENERIC,
          '',
          notification
        );
      } else {
        this.queueInAppToast(notification);
      }
    }
  }

  private queueMatchResultsToast(notification: SKZNotification) {
    console.debug('PushNotificationController: queue match results toast');
    this.shouldStoreTournamentResult((shouldStore: boolean) => {
      console.debug(
        'PushNotificationController: should store tournament result before queue: ' + shouldStore
      );
      if (!shouldStore) {
        const params: Object = {
          action: DeepLinkTypes.SHOW_MATCH_RESULTS,
          tournamentId: notification.getTournamentId(),
        };
        this.addNotifToastToQueue(
          notification.getMessageTitle() || '',
          notification.getMessageBody(),
          NotificationIconImages.MATCH_RESULT,
          params,
          notification
        );
      }
    });
  }

  private queueInAppToast(notification: SKZNotification) {
    const notifId: string | undefined = notification.getNotificationId();
    let iconImage = NotificationIconImages.GENERIC;
    let action = notification.getAction();
    let params: Object = {};
    let didSetForNotif = false;
    if (notifId != undefined && typeof notifId == 'string') {
      if (notification.isNotificationIdEqualToType(NotificationTypes.REMATCH)) {
        iconImage = NotificationIconImages.REMATCH;
        didSetForNotif = true;
      } else if (notification.isLeaderboardNotification()) {
        iconImage = NotificationIconImages.LEADERBOARD;
        action = DeepLinkTypes.SHOW_LEADERBOARDS;
        didSetForNotif = true;
      } else if (
        notification.isNotificationIdEqualToType(NotificationTypes.TROPHY_AWARDED_IN_APP)
      ) {
        this.queueTrophyEarnedModalForTrophyId(notification.getTrophyId() || '');
        iconImage = NotificationIconImages.TROPHY;
        action = DeepLinkTypes.SHOW_TROPHIES;
        didSetForNotif = true;
      }
    }

    if (!didSetForNotif && this.isADeepLinkToast(notification)) {
      params = {deepLinkUrl: notification.getDeepLink()};
    } else {
      params = {action: action};
    }

    this.addNotifToastToQueue(
      notification.getMessageTitle() || '',
      notification.getMessageBody(),
      iconImage,
      params,
      {}
    );
  }

  private isADeepLinkToast(notification: SKZNotification): boolean {
    const notifDeepLink: string | undefined = notification.getDeepLink();
    return (
      notifDeepLink != null &&
      typeof notifDeepLink == 'string' &&
      !notification.isActionEqualToType(NotificationTypes.CHAT) &&
      !notification.isActionEqualToType(NotificationTypes.TROPHY_EARNED)
    );
  }

  private addChatNotifToastToQueue(notification: SKZNotification) {
    const toast: ChatToast = notification.getChatToastFromNotif();
    if (toast.fromContact?.length > 0 && toast.messageBody?.length > 0) {
      this.pushNotifToQueueAndDisplay(notification.getChatToastFromNotif());
    }
  }

  private addTrophyEarnedNotifToQueue(notification: SKZNotification) {
    this.queueTrophyEarnedModalForTrophyId(notification.getTrophyId() || '');

    const params: Object = {
      action: DeepLinkTypes.SHOW_TROPHIES,
    };

    this.addNotifToastToQueue(
      notification.getMessageTitle() || '',
      notification.getMessageBody(),
      NotificationIconImages.TROPHY,
      params,
      {}
    );
  }

  private addNotifToastToQueue(
    message: string,
    subtext: string,
    iconResource: string,
    action: Object,
    params: Object
  ) {
    const toast: GeneralToast = {message, subtext, iconResource, action, params};

    this.pushNotifToQueueAndDisplay(toast);
  }

  private pushNotifToQueueAndDisplay(toast: Toast) {
    console.debug(
      'PushNotificationController: pushNotifToQueueAndDisplay: ' + JSON.stringify(toast)
    );
    this.notifToastsToDisplay.push(toast);

    // If no toast is being actively displayed, start displaying the queue
    console.debug('PushNotificationController: should start display: ' + this.isDisplayingToast);
    console.debug('PushNotificationController: should fire next toast: ' + this.canFireNextToast);
    this.displayQueue();
  }

  public displayQueue() {
    if (!this.isDisplayingToast) {
      this.startToastDisplay();
    } else {
      if (this.canFireNextToast) {
        this.triggerToastTimer();
      }
    }
  }

  private startToastDisplay() {
    if (ModalNavigationController.isModalShown()) {
      setTimeout(() => {
        this.startToastDisplay();
      }, 500);
      return;
    }
    this.isDisplayingToast = true;
    this.canFireNextToast = false;

    SkillzModule.shouldInteractWithInterface(
      (shouldInteract: boolean, isMatchInProgress: boolean): void => {
        if (
          !shouldInteract ||
          !FTUEController.hasSeenMatchStreamTutorial() ||
          this.notifToastsToDisplay.length < 1
        ) {
          this.isDisplayingToast = false;
          this.canFireNextToast = false;
          return;
        }

        const firstToast: Toast | undefined = _.head(this.notifToastsToDisplay);
        this.notifToastsToDisplay = _.drop(this.notifToastsToDisplay);
        const isChatToast: boolean = _.get(firstToast, 'fromContact');
        const isTrophyToast: boolean = _.get(firstToast, 'trophy');

        if (isChatToast) {
          AWSManager.trackEvent('IN_SKILLZ_NOTIFICATION_TOAST_DISPLAYED');
          this.pushToastToDisplay(
            _.get(firstToast, 'fromContact'),
            _.get(firstToast, 'messageBody'),
            '',
            'chat',
            _.get(firstToast, 'params')
          );
        } else if (isTrophyToast) {
          const trophyToast = _.get(firstToast, 'trophy');
          AWSManager.trackEvent('IN_SKILLZ_NOTIFICATION_TROPHY_TOAST_DISPLAYED', {
            id: _.get(trophyToast, 'id'),
          });
          this.pushToastToDisplay('', '', '', 'trophy', undefined, trophyToast);
        } else {
          AWSManager.trackEvent('IN_SKILLZ_NOTIFICATION_TOAST_DISPLAYED');
          const params = Platform.select({
            ios: firstToast?.params?.deepLinkUrl
              ? {deepLinkUrl: firstToast.params.deepLinkUrl}
              : firstToast?.action || '',
            android: _.get(firstToast, 'params.deepLinkUrl', ''),
          });
          this.pushToastToDisplay(
            _.get(firstToast, 'message'),
            _.get(firstToast, 'subtext'),
            _.get(firstToast, 'iconResource'),
            'general',
            params
          );
        }
      }
    );
  }

  private pushToastToDisplay(
    primaryText: string,
    secondaryText: string,
    icon: string,
    toastType: 'chat' | 'trophy' | 'general',
    actionParams: object | string | undefined,
    trophy?: SKZTrophy
  ) {
    console.debug(
      'PushNotificationController: pushing toast to display: ' + primaryText + ' ' + secondaryText
    );
    const toast: React.ReactElement = (
      <NotificationToast
        primaryText={primaryText}
        secondaryText={secondaryText}
        icon={icon}
        toastType={toastType}
        actionParams={actionParams}
        onDismiss={() => {
          this.dismissDisplayedNotification();
        }}
        trophy={trophy}
      />
    );

    NotificationNavigationController.showInAppToast(toast);
    this.startIntervalTimer();
  }

  private startIntervalTimer() {
    if (this.intervalTimer != 0) {
      clearTimeout(this.intervalTimer);
    }
    this.intervalTimer = 0;
    this.intervalTimer = setTimeout(() => {
      console.debug('PushNotificationController: triggering toast timer after interval');
      this.triggerToastTimer();
    }, ClientPropertyConstants.getInAppNotificationDisplayInterval());
  }

  private triggerToastTimer() {
    if (!ModalNavigationController.isModalShown()) {
      this.canFireNextToast = true;
      if (this.notifToastsToDisplay.length > 0) {
        this.triggerDismissal(() => {
          this.startToastDisplay();
        });
      } else {
        const durationRemaining: number =
          ClientPropertyConstants.getInAppNotificationDisplayLength() -
          ClientPropertyConstants.getInAppNotificationDisplayInterval();
        if (this.durationTimer != 0) {
          clearTimeout(this.durationTimer);
        }
        if (this.intervalTimer != 0) {
          clearTimeout(this.intervalTimer);
        }
        this.intervalTimer = 0;
        this.durationTimer = 0;
        this.durationTimer = setTimeout(() => {
          this.triggerDismissal(() => {
            if (this.notifToastsToDisplay.length > 0) {
              this.startToastDisplay();
            } else {
              this.processStoredTournamentResults();
            }
          });
        }, durationRemaining) as unknown as number;
      }
    } else {
      setTimeout(() => {
        this.triggerToastTimer();
      }, 300);
    }
  }

  private triggerDismissal(completion: () => void) {
    this.canFireNextToast = false;
    this.isDisplayingToast = false;
    GlobalEventEmitter.emit('DismissNotificationDisplayed');
    setTimeout(() => {
      if (completion) {
        completion();
      }
    }, 300);
  }

  private dismissDisplayedNotification() {
    NotificationNavigationController.hideInAppToast();
  }

  /**
   * NOTE: This method has been deprecated in favor of an updated and full OTA version called
   * getTrophyToastsAndInsertIntoQueue. We are keeping this method here and commenting out the code
   * because there are native calls of this function, and we don't want to break the bridge by completely
   * removing it.
   */
  public queueTrophyEarnedModalForTrophyId(_trophyId: string) {
    // if (this.shouldShowTrophyAwardedModal()) {
    //   const trophies: string[] =
    //     Preferences.get(SKZConstants.IN_APP_TOAST_EARNED_TROPHY_QUEUE) ?? [];
    //   const indexOf = _.indexOf(trophies, trophyId);
    //   const nonDupedTrophies = _.slice(trophies);
    //   if (indexOf == -1 && trophyId.length > 0) {
    //     nonDupedTrophies.push(trophyId);
    //   }
    //   Preferences.set({[SKZConstants.IN_APP_TOAST_EARNED_TROPHY_QUEUE]: nonDupedTrophies});
    // }
  }

  /**
   * Function that facilitates showing Trophy Progression Toasts by doing the following:
   * 1. Getting called where we want to show the toasts (currently, just on the Results screen)
   * 2. Getting trophy data from Async storage (currently, just saved on each match join)
   * 3. Getting new, up-to-date trophy data
   * 4. Checking the diff between trophy data from #2 and #3, and getting which trophies should be toasted
   * 5. Adding those trophies to toast to the front of the this.notifToastsToDisplay queue, to be shown first
   */
  public async getTrophyToastsAndInsertIntoQueue() {
    const trophyDataFromStorage = getTrophyDataFromStorage();
    const newTrophyData = await fetchTrophyData();
    const addExtraCheckpoint =
      ClientPropertyConstants.getH2HResultsTrophiesEnabled() &&
      ClientPropertyConstants.getH2HResultsExperimentEnabled() &&
      NavigationManager.isSceneCurrentlyPresented(ScreenNames.RESULT);

    const trophiesToToast = getProgressedTrophiesToToast(
      newTrophyData,
      trophyDataFromStorage,
      addExtraCheckpoint
    );
    console.debug(
      `PushNotificationController: Trophy Progress to toast: ${JSON.stringify(trophiesToToast)}`
    );
    const trophyToasts: Toast[] = [];

    trophiesToToast.forEach((trophyToToast) => {
      trophyToasts.push({trophy: trophyToToast});
    });

    if (trophyToasts.length > 0) {
      const trophyToastsClone = _.clone(trophyToasts) as TrophyToast[];
      const areAwardedTrophyToasts = trophyToastsClone.some((trophyToast) =>
        trophyToast.trophy.isAwarded()
      );
      const notifToastsClone = _.clone(this.notifToastsToDisplay);
      this.notifToastsToDisplay = trophyToasts.concat(notifToastsClone);
      if (areAwardedTrophyToasts) {
        this.removeTrophyEarnedToastsFromQueue();
      }
    }
  }

  public removeAllToasts() {
    if (this.notifToastsToDisplay && this.notifToastsToDisplay.length) {
      this.notifToastsToDisplay = [];
    }
  }

  /**
   * This function only works for iOS. As of now, the Android native code does not process trophyEarned
   * toasts differently from regular toasts, so there is no way to filter them out at this time.
   * We are filtering by the toast's iconResource because there are multiple iOS notification types
   * that deal with trophy award, and at this point in the notification process, iconResource is a safe
   * and correct way to know that the toast is a trophyEarned toast
   */
  private removeTrophyEarnedToastsFromQueue() {
    const toastsWithoutTrophyEarned = this.notifToastsToDisplay.filter(
      (toast) => !(toast.iconResource && toast.iconResource === NotificationIconImages.TROPHY)
    );

    this.notifToastsToDisplay = toastsWithoutTrophyEarned;
  }
}

const PushNotificationControllerInstance = new PushNotificationController();

export default PushNotificationControllerInstance;
